import { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { useAuth } from '../../../hooks/useAuth';

const FORCES = [
  { id: 'threat_new_entrants', label: 'Threat of New Entrants' },
  { id: 'bargaining_power_suppliers', label: 'Bargaining Power of Suppliers' },
  { id: 'bargaining_power_buyers', label: 'Bargaining Power of Buyers' },
  { id: 'threat_substitutes', label: 'Threat of Substitutes' },
  { id: 'competitive_rivalry', label: 'Competitive Rivalry' },
];

const RATING_COLORS = {
  Low: { background: '#e8f5ee', color: '#2d7a4f', border: '#2d7a4f' },
  Moderate: { background: '#fff8e1', color: '#c9952a', border: '#c9952a' },
  High: { background: '#fde8e8', color: '#c0392b', border: '#c0392b' },
};

export default function PortersFramework({ dealId }) {
  const { user, userProfile } = useAuth();
  const [forces, setForces] = useState(() => {
    const initial = {};
    FORCES.forEach((f) => { initial[f.id] = { rating: 'Moderate', analysis: '' }; });
    return initial;
  });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDoc(doc(db, 'deals', dealId, 'frameworks', 'porters'))
      .then((snap) => {
        if (snap.exists() && snap.data().forces) {
          setForces(snap.data().forces);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dealId]);

  function setForceField(forceId, field, value) {
    setForces((prev) => ({
      ...prev,
      [forceId]: { ...prev[forceId], [field]: value },
    }));
  }

  async function handleSave() {
    setSaving(true);
    setSaveMsg('');
    try {
      await setDoc(doc(db, 'deals', dealId, 'frameworks', 'porters'), {
        completed_by: user?.uid || '',
        completed_by_name: userProfile?.full_name || userProfile?.display_name || '',
        completed_at: new Date().toISOString(),
        forces,
      });
      setSaveMsg('Saved successfully');
    } catch (err) {
      setSaveMsg('Save failed: ' + (err.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div style={{ padding: 16, color: 'var(--color-text-secondary)', fontSize: 13 }}>Loading...</div>;
  }

  return (
    <div>
      <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 20, lineHeight: 1.6 }}>
        Assess each competitive force using the Porter&apos;s Five Forces framework.
        Rate each force and provide a brief analysis.
      </p>

      {FORCES.map((force) => {
        const current = forces[force.id] || { rating: 'Moderate', analysis: '' };
        const ratingStyle = RATING_COLORS[current.rating] || RATING_COLORS.Moderate;
        return (
          <div key={force.id} className="card mb-4">
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>{force.label}</h3>
              <select
                value={current.rating}
                onChange={(e) => setForceField(force.id, 'rating', e.target.value)}
                style={{
                  padding: '4px 10px',
                  borderRadius: 6,
                  border: `1px solid ${ratingStyle.border}`,
                  background: ratingStyle.background,
                  color: ratingStyle.color,
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                <option value="Low">Low</option>
                <option value="Moderate">Moderate</option>
                <option value="High">High</option>
              </select>
            </div>
            <div className="card-body">
              <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 6 }}>
                Analysis
              </label>
              <textarea
                className="form-textarea"
                value={current.analysis}
                onChange={(e) => setForceField(force.id, 'analysis', e.target.value)}
                placeholder={`Describe the ${force.label.toLowerCase()} for this company's market...`}
                rows={3}
              />
            </div>
          </div>
        );
      })}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '8px 20px',
            borderRadius: 6,
            border: '1px solid var(--color-accent)',
            background: 'var(--color-accent)',
            color: '#fff',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: 13,
            fontWeight: 600,
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? 'Saving...' : 'Save Framework'}
        </button>
        {saveMsg && (
          <span style={{
            fontSize: 13,
            color: saveMsg.includes('failed') ? 'var(--color-error)' : 'var(--color-success)',
          }}>
            {saveMsg}
          </span>
        )}
      </div>
    </div>
  );
}
